import React, { useMemo, useEffect, useState, useCallback } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { getThemeById } from '../../config/themes'
import type { TenantThemeConfig } from '../../types/theme'
import * as authService from '../../services/authService'
import {
  USER_SUMMARY_UPDATED_EVENT,
  POINTS_CONFIG_UPDATED_EVENT,
  getActivePointsRules,
  getActiveRewards,
  getPointsConfig,
  type PointsConfigData,
  type PointsReward,
  type PointsRule,
} from '../../services/pointsService'
import { usePredictionUserEntry } from '../../hooks/usePredictionUserEntry'
import { useRedEnvelopeUserEntry } from '../../hooks/useRedEnvelopeUserEntry'
import { useLuckyWheelUserEntry } from '../../hooks/useLuckyWheelUserEntry'
import { useBerThongUserEntry } from '../../hooks/useBerThongUserEntry'
import { useScratchLotteryUserEntry } from '../../hooks/useScratchLotteryUserEntry'
import { useHuayJackpotUserEntry } from '../../hooks/useHuayJackpotUserEntry'
import './pointsLayout.css'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

export interface PointsOutletContext {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
  getAssetUrl: (path: string) => string
  pointsText: string
  pointsBalance: number
  /** ยอดเงินสดในกระเป๋า (บาท) */
  cashBalance: number
  pointsConfig: PointsConfigData | null
  activeCashToPointsRules: PointsRule[]
  activePointsToCashRules: PointsRule[]
  activeRewards: PointsReward[]
  refreshUserSummary: () => Promise<void>
}

interface ModernPointsLayoutProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

type PointsTab = {
  to: string
  sub: string
  label: string
  fa: string
  tone: 'money' | 'points' | 'reward' | 'history' | 'prediction' | 'red-envelope' | 'lucky-wheel' | 'ber-thong' | 'scratch-lottery' | 'huay-jackpot'
}

const BASE_TABS: PointsTab[] = [
  { to: 'moneytopoint', fa: 'fas fa-exchange-alt', tone: 'money', sub: 'แลกเปลี่ยน', label: 'เงินเป็นคะแนน' },
  { to: 'pointtomoney', fa: 'fas fa-coins', tone: 'points', sub: 'แลกเปลี่ยน', label: 'คะแนนเป็นเงิน' },
  { to: 'rewards', fa: 'fas fa-gift', tone: 'reward', sub: 'แลกรางวัล', label: 'ของรางวัล' },
  { to: 'reward-history', fa: 'fas fa-history', tone: 'history', sub: 'ประวัติ', label: 'แลกรางวัล' },
]

const PREDICTION_TAB: PointsTab = {
  to: 'games/prediction',
  fa: 'fas fa-bullseye',
  tone: 'prediction',
  sub: 'เกมทายผล',
  label: 'Prediction',
}

const RED_ENVELOPE_TAB: PointsTab = {
  to: 'games/red-envelope',
  fa: 'fas fa-envelope',
  tone: 'red-envelope',
  sub: 'เกมซองแดง',
  label: 'ซองแดง',
}

const LUCKY_WHEEL_TAB: PointsTab = {
  to: 'games/lucky-wheel',
  fa: 'fas fa-dharmachakra',
  tone: 'lucky-wheel',
  sub: 'เกมวงล้อ',
  label: 'วงล้อมหาโชค',
}

const BER_THONG_TAB: PointsTab = {
  to: 'games/ber-thong',
  fa: 'fas fa-th',
  tone: 'ber-thong',
  sub: 'เบอร์เงินสด',
  label: 'เบอร์ทอง',
}

const SCRATCH_LOTTERY_TAB: PointsTab = {
  to: 'games/scratch-lottery',
  fa: 'fas fa-ticket-alt',
  tone: 'scratch-lottery',
  sub: 'เกมขูดลุ้นโชค',
  label: 'หวยขูดเมกา',
}

const HUAY_JACKPOT_TAB: PointsTab = {
  to: 'games/huay-jackpot',
  fa: 'fas fa-bullseye',
  tone: 'huay-jackpot',
  sub: 'เกม 5+1',
  label: 'หวยแจ็กพอต',
}

/** Layout หน้าคะแนน (points): hero + สรุปคะแนน + แท็บ + Outlet */
const ModernPointsLayout: React.FC<ModernPointsLayoutProps> = ({ themeConfig, assetConfig }) => {
  const [userSummary, setUserSummary] = useState<authService.UserInfoSummary | null>(null)
  const [pointsConfig, setPointsConfig] = useState<PointsConfigData | null>(null)

  const themeFolder = useMemo(() => getThemeById(themeConfig.themeId)?.folder ?? 'HuayToday-Blue', [themeConfig.themeId])
  const { showPredictionEntry } = usePredictionUserEntry(themeConfig.settings.predictionEnabled)
  const { showRedEnvelopeEntry } = useRedEnvelopeUserEntry(themeConfig.settings.redEnvelopeEnabled)
  const { showLuckyWheelEntry } = useLuckyWheelUserEntry(themeConfig.settings.luckyWheelEnabled)
  const { showBerThongEntry } = useBerThongUserEntry(themeConfig.settings.berThongEnabled)
  const { showScratchLotteryEntry } = useScratchLotteryUserEntry(themeConfig.settings.scratchLotteryEnabled)
  const { showHuayJackpotEntry } = useHuayJackpotUserEntry(themeConfig.settings.huayJackpotEnabled)
  const tabs = useMemo(() => {
    let next = [...BASE_TABS]
    if (showPredictionEntry) {
      next = [...next, PREDICTION_TAB]
    }
    if (showRedEnvelopeEntry) {
      next = [...next, RED_ENVELOPE_TAB]
    }
    if (showScratchLotteryEntry) {
      next = [...next, SCRATCH_LOTTERY_TAB]
    }
    if (showHuayJackpotEntry) {
      next = [...next, HUAY_JACKPOT_TAB]
    }
    if (showLuckyWheelEntry) {
      next = [...next, LUCKY_WHEEL_TAB]
    }
    if (showBerThongEntry) {
      next = [...next, BER_THONG_TAB]
    }
    return next
  }, [showPredictionEntry, showRedEnvelopeEntry, showScratchLotteryEntry, showHuayJackpotEntry, showLuckyWheelEntry, showBerThongEntry])
  const getAssetUrl = (path: string) => {
    const base = assetConfig.CDN_BASE_URL ? `${assetConfig.CDN_BASE_URL}/${themeFolder}` : `/${themeFolder}`
    return base + (path.startsWith('/') ? path : `/${path}`)
  }

  const refreshUserSummary = useCallback(async () => {
    if (!authService.isAuthenticated()) {
      setUserSummary(null)
      return
    }
    const summary = await authService.fetchUserInfoSummary({ force: true })
    setUserSummary(summary)
  }, [])

  useEffect(() => {
    void refreshUserSummary()
  }, [refreshUserSummary])

  useEffect(() => {
    const tenantId = themeConfig.tenantId
    if (!tenantId || !authService.isAuthenticated()) {
      setPointsConfig(null)
      return
    }
    getPointsConfig(tenantId).then(setPointsConfig)
  }, [themeConfig.tenantId])

  const refreshPointsConfig = useCallback(async () => {
    const tenantId = themeConfig.tenantId
    if (!tenantId || !authService.isAuthenticated()) {
      setPointsConfig(null)
      return
    }
    const config = await getPointsConfig(tenantId)
    setPointsConfig(config)
  }, [themeConfig.tenantId])

  useEffect(() => {
    const handlePointsConfigUpdated = (event: Event) => {
      const detail = (event as CustomEvent<{ tenantId?: string }>).detail
      if (detail?.tenantId && detail.tenantId !== themeConfig.tenantId) return
      void refreshPointsConfig()
    }
    window.addEventListener(POINTS_CONFIG_UPDATED_EVENT, handlePointsConfigUpdated)
    return () => window.removeEventListener(POINTS_CONFIG_UPDATED_EVENT, handlePointsConfigUpdated)
  }, [refreshPointsConfig, themeConfig.tenantId])

  useEffect(() => {
    const handleUserSummaryUpdated = () => {
      void refreshUserSummary()
    }
    window.addEventListener(USER_SUMMARY_UPDATED_EVENT, handleUserSummaryUpdated)
    return () => window.removeEventListener(USER_SUMMARY_UPDATED_EVENT, handleUserSummaryUpdated)
  }, [refreshUserSummary])

  const pointsText = userSummary?.points != null ? `${Number(userSummary.points).toFixed(2)} Pts` : '0.00 Pts'
  const pointsBalance = Number(userSummary?.points ?? 0)
  const cashBalance = Number(userSummary?.balance ?? 0)
  const context: PointsOutletContext = {
    themeConfig,
    assetConfig,
    getAssetUrl,
    pointsText,
    pointsBalance,
    cashBalance,
    pointsConfig,
    activeCashToPointsRules: getActivePointsRules(pointsConfig?.cashToPointsRules),
    activePointsToCashRules: getActivePointsRules(pointsConfig?.pointsToCashRules),
    activeRewards: getActiveRewards(pointsConfig?.rewards),
    refreshUserSummary,
  }

  return (
    <div className="content htd-pts">
      <div className="htd-pts__glow" aria-hidden />
      <div className="htd-pts__inner">
        <header className="htd-pts__hero htd-pts-anim htd-pts-anim--1">
          <div className="htd-pts__badge" aria-hidden>
            <i className="fas fa-star" />
          </div>
          <div>
            <p className="htd-pts__eyebrow">Points Hub</p>
            <h1 className="htd-pts__title">คะแนนของคุณ</h1>
            <p className="htd-pts__desc">แลกเงินเป็นคะแนน แลกของรางวัล และติดตามประวัติได้จากที่เดียว</p>
          </div>
        </header>

        <section className="htd-pts__balance htd-pts-anim htd-pts-anim--2" aria-label="ยอดคะแนน">
          <p className="htd-pts__balance-label">คะแนนคงเหลือ</p>
          <p className="htd-pts__balance-value">{pointsText}</p>
          <p className="htd-pts__note">
            <strong>หมายเหตุ:</strong> ขอสงวนสิทธิ์ในการเปลี่ยนแปลงอัตราหรือปิดระบบรับแลกเปลี่ยนคะแนน
            โดยมิต้องแจ้งให้ทราบล่วงหน้า
          </p>
        </section>

        <nav
          className={`htd-pts__tabs${tabs.length >= 5 ? ' htd-pts__tabs--5' : ''} htd-pts-anim htd-pts-anim--2`}
          aria-label="แท็บคะแนน"
        >
          {tabs.map((tab, index) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.to === 'moneytopoint'}
              className={({ isActive }) =>
                `htd-pts__tab htd-pts__tab--${tab.tone}${isActive ? ' is-active' : ''}`
              }
              style={{ animationDelay: `${0.06 + index * 0.04}s` }}
            >
              <span className="htd-pts__tab-icon" aria-hidden>
                <i className={tab.fa} />
              </span>
              <span className="text-center">
                <span className="htd-pts__tab-sub">{tab.sub}</span>
                <span className="htd-pts__tab-label">{tab.label}</span>
              </span>
            </NavLink>
          ))}
        </nav>

        <div className="htd-pts__outlet htd-pts-anim htd-pts-anim--3">
          <Outlet context={context} />
        </div>
      </div>
    </div>
  )
}

export default ModernPointsLayout
